import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel } from "devextreme-react";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";

import routes from "../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../common/Messages";
import { DisableEnableControls, loadLookupFromStaticDataSource } from "../../../../common/Utils";
import { useCommittedFilterRows } from "../../../../common/reportFilters";
import { TransactionStatus, YesNo } from "../../../../enum/Enum";
import CalculateTab from "./tabs/CalculateTab";
import "./calculateDeferredRevenue.css";
import DeferredRevenueResultGrid from "./common/DeferredRevenueResultGrid";
import {
    BY_CUSTOMER_COLUMNS,
    BY_CUSTOMER_DEPT_COLUMNS,
    DETAIL_COLUMNS,
    SUMMARY_COLUMNS,
} from "./ResultGridColumns";
import {
    ACCT_SOURCE_OPTIONS,
    AcctSource,
    DEFERRED_REVENUE_HELP,
    FIELD,
    PAGE_TITLE,
    REQUEST_TIMEOUT_MS,
    STATUS_OPTIONS,
    TAB,
    USE_MGT_UNITS_OPTIONS,
} from "./constants";

const BUSY = Object.freeze({ IDLE: "idle", CALCULATING: "calculating", CREATING: "creating" });

const KEY_EXPR = "ClientKey";

/**
 * Stamps a synthetic key on each row. The stored procedure returns no natural unique key — the
 * Summary set groups by five nullable management units, and Detail has no unique tuple at all.
 */
const withKeys = (rows) =>
    (Array.isArray(rows) ? rows : []).map((row, index) => ({ ...row, [KEY_EXPR]: index }));

const initialFormFields = {
    AsOfDate: new Date(),
    Status: TransactionStatus.Open,
    Reference: "",
    AcctSource: AcctSource.ChargeCode,
    DebitAccount: null,
    DebitActivity: null,
    UseMgtUnits: YesNo.Yes,
    CreditAccount: null,
    CreditActivity: null,
};

/**
 * WM "Calculate Deferred Handling Revenue" (legacy WMD0088).
 *
 * Start runs SPWM_DEFERREDREVENUECHARGES four times server-side and fills all four result grids from
 * one response. Create posts an unposted AC reversing journal built from a server-side re-read of the
 * Summary set — the client never sends money rows or GL pointers.
 *
 * The legacy print/preview reports are not migrated; the two Print buttons are kept for parity but
 * are permanently disabled.
 */
const CalculateDeferredRevenue = (props) => {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const formRefs = useRef({});
    const reportRef = useRef(null);

    const [formFields, setFormFields] = useState(initialFormFields);
    const [filterWhereInfoList, setFilterWhereInfoList] = useState([]);
    const [activeTab, setActiveTab] = useState(TAB.CALCULATE);
    const [busy, setBusy] = useState(BUSY.IDLE);
    const [hasCalculated, setHasCalculated] = useState(false);

    const [summaryRows, setSummaryRows] = useState([]);
    const [detailRows, setDetailRows] = useState([]);
    const [customerRows, setCustomerRows] = useState([]);
    const [customerDeptRows, setCustomerDeptRows] = useState([]);

    // Echoed back on Create as a drift guard; a mismatch aborts the post server-side.
    const [expectedSummary, setExpectedSummary] = useState({ rowCount: null, totalAmount: null });

    const isBusy = busy !== BUSY.IDLE;

    const postOptions = useCallback(
        () => ({
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
            timeout: REQUEST_TIMEOUT_MS,
        }),
        [accessToken]
    );

    /**
     * The global axios interceptor reads error.response.status without guarding, so a timeout (where
     * response is undefined) throws inside the interceptor and that TypeError is what reaches us.
     */
    const apiError = (error, fallback) =>
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : null) ||
        error?.message ||
        fallback ||
        MESSAGES.UNKNOWN_ERROR_OCCURRED;

    const clearResults = useCallback(() => {
        setSummaryRows([]);
        setDetailRows([]);
        setCustomerRows([]);
        setCustomerDeptRows([]);
        setExpectedSummary({ rowCount: null, totalAmount: null });
    }, []);

    /**
     * CommonReport fires this once, from loadReport's .then. It stays correct because the editable
     * filter grid is bound to an ArrayStore wrapping this same array instance, which cell edits mutate
     * in place — so the reference captured here stays live. Do not clone it per render.
     */
    const handleReportStateChange = useCallback((reportState) => {
        setFilterWhereInfoList(reportState?.filterWhereInfoList ?? []);
    }, []);

    // Acct Source = Charge Code disables the debit lookups; User Entered enables them
    // (uWMD0088.pas:697-714).
    const applyAcctSourceRules = useCallback((acctSource) => {
        const enabled = acctSource === AcctSource.UserEntered;
        DisableEnableControls(FIELD.DebitAccount, !enabled, formRefs);
        DisableEnableControls(FIELD.DebitActivity, !enabled, formRefs);
    }, []);

    const handleFieldChange = useCallback(
        (name, value) => {
            if (name === FIELD.AcctSource) {
                applyAcctSourceRules(value);
                if (value !== AcctSource.UserEntered) {
                    // The legacy clears as well as disables (uWMD0088.pas:703-707).
                    setFormFields((previous) => ({ ...previous, DebitAccount: null, DebitActivity: null }));
                }
            }
        },
        [applyAcctSourceRules]
    );

    const handleFormLoad = useCallback(() => {
        // FormField calls onFormLoad in its fetchData finally, before React has committed the child
        // Fields, so formRefs are not populated yet. Same shape as weekSettings/tabs/PrimaryTab.js.
        setTimeout(() => {
            loadLookupFromStaticDataSource(FIELD.Status, STATUS_OPTIONS, formFields, formRefs);
            loadLookupFromStaticDataSource(FIELD.AcctSource, ACCT_SOURCE_OPTIONS, formFields, formRefs);
            loadLookupFromStaticDataSource(FIELD.UseMgtUnits, USE_MGT_UNITS_OPTIONS, formFields, formRefs);
            applyAcctSourceRules(formFields.AcctSource);
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Belt-and-braces re-apply, mirroring weekSettings/tabs/PrimaryTab.js.
    useEffect(() => {
        applyAcctSourceRules(formFields.AcctSource);
    }, [formFields.AcctSource, applyAcctSourceRules]);

    /**
     * The four legacy Start validations in order, each jumping back to the Calculate tab, showing a
     * message and focusing the offending field (uWMD0088.pas:333-372).
     */
    const failValidation = useCallback((message, fieldName) => {
        setActiveTab(TAB.CALCULATE);
        notify({ message, width: 450 }, "error", 4000);
        // The tab switch has not painted yet, so focusing immediately is a no-op.
        setTimeout(() => {
            const ref = formRefs.current?.[fieldName];
            if (ref && typeof ref.instance === "function") {
                ref.instance().focus();
            }
        }, 0);
        return false;
    }, []);

    const validate = useCallback(() => {
        if (!formFields.AsOfDate) {
            return failValidation(MESSAGES.DEFERRED_REVENUE_AS_OF_DATE_REQUIRED, FIELD.AsOfDate);
        }
        if (!formFields.Status) {
            return failValidation(MESSAGES.DEFERRED_REVENUE_STATUS_REQUIRED, FIELD.Status);
        }
        if (formFields.AcctSource === AcctSource.UserEntered && !formFields.DebitAccount) {
            return failValidation(MESSAGES.DEFERRED_REVENUE_DEBIT_ACCOUNT_REQUIRED, FIELD.DebitAccount);
        }
        if (!formFields.CreditAccount) {
            return failValidation(MESSAGES.DEFERRED_REVENUE_CREDIT_ACCOUNT_REQUIRED, FIELD.CreditAccount);
        }
        return true;
    }, [formFields, failValidation]);

    // Commits any in-progress filter cell before reading the rows — see common/reportFilters.js for why
    // the onReportStateChange array is the wrong source at submit time. The ref reaches CommonReport
    // indirectly here, through CalculateTab's reportRef -> innerRef.
    const readFilterRows = useCommittedFilterRows(reportRef, filterWhereInfoList);

    const buildRequest = useCallback(
        (rows, extra) => ({
            AsOfDate: formFields.AsOfDate,
            Status: formFields.Status,
            Reference: formFields.Reference || "",
            AcctSource: formFields.AcctSource,
            UseManagementUnits: formFields.UseMgtUnits,
            DebitAccountRowId:
                formFields.AcctSource === AcctSource.UserEntered ? formFields.DebitAccount : null,
            DebitActivityRowId:
                formFields.AcctSource === AcctSource.UserEntered ? formFields.DebitActivity : null,
            CreditAccountRowId: formFields.CreditAccount,
            CreditActivityRowId: formFields.CreditActivity,
            FilterWhereInfoList: rows || [],
            ...extra,
        }),
        [formFields]
    );

    const handleStart = useCallback(async () => {
        if (!validate()) {
            return;
        }

        // Commits any in-progress filter cell first, so Calculate matches what the user typed.
        const filterRows = await readFilterRows();

        setBusy(BUSY.CALCULATING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMDeferredRevenue.Calculate}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;
            if (payload?.Success === false) {
                clearResults();
                setHasCalculated(false);
                notify({ message: payload.Message || MESSAGES.ERROR, width: 450 }, "error", 5000);
                return;
            }

            const data = payload?.Resultdata ?? {};
            setSummaryRows(withKeys(data.Summary));
            setDetailRows(withKeys(data.Detail));
            setCustomerRows(withKeys(data.ByCustomer));
            setCustomerDeptRows(withKeys(data.ByCustomerDepartment));
            setExpectedSummary({
                rowCount: data.SummaryRowCount ?? null,
                totalAmount: data.SummaryTotalAmount ?? null,
            });
            setHasCalculated(true);

            if (data.CanCreate) {
                setActiveTab(TAB.SUMMARY);
            } else {
                // Legacy jumped to Summary regardless and merely disabled Create; staying put is a
                // small deliberate improvement.
                notify({ message: MESSAGES.DEFERRED_REVENUE_NO_RESULTS, width: 450 }, "warning", 4000);
            }
        } catch (error) {
            // Stale results are worse than none — they would let Create run against parameters that
            // never succeeded.
            clearResults();
            setHasCalculated(false);
            notify({ message: apiError(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [validate, readFilterRows, buildRequest, clearResults, postOptions]);

    const handleCreate = useCallback(async () => {
        if (!validate()) {
            return;
        }

        // Committed here too, so the drift guard compares against the same filter the user can see.
        const filterRows = await readFilterRows();

        setBusy(BUSY.CREATING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMDeferredRevenue.CreateJournalEntries}`,
                JSON.stringify(
                    buildRequest(filterRows, {
                        ExpectedSummaryRowCount: expectedSummary.rowCount,
                        ExpectedSummaryTotalAmount: expectedSummary.totalAmount,
                    })
                ),
                postOptions()
            );

            const payload = response?.data;
            if (payload?.Success === false) {
                // Results are deliberately left in place so Create stays enabled for a retry
                // (uWMD0088.pas:686).
                notify(
                    { message: payload.Message || MESSAGES.AC_JOURNAL_CREATE_ERROR, width: 450 },
                    "error",
                    5000
                );
                return;
            }

            const recordNumber = payload?.Resultdata?.RecordNumber;
            notify(
                {
                    message: recordNumber
                        ? `${MESSAGES.AC_JOURNAL_CREATED} (${recordNumber})`
                        : MESSAGES.AC_JOURNAL_CREATED,
                    width: 450,
                },
                "success",
                4000
            );

            // Clearing the results disables Create, matching the legacy button state after a
            // successful post, and the legacy returns to the Calculate tab (uWMD0088.pas:491).
            clearResults();
            setHasCalculated(false);
            setActiveTab(TAB.CALCULATE);
        } catch (error) {
            notify(
                { message: apiError(error, MESSAGES.AC_JOURNAL_CREATE_ERROR), width: 450 },
                "error",
                5000
            );
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [validate, readFilterRows, buildRequest, expectedSummary, clearResults, postOptions]);

    const handleReset = useCallback(() => {
        setFormFields(initialFormFields);
        clearResults();
        setHasCalculated(false);
        setActiveTab(TAB.CALCULATE);
        // Re-fetches the field definitions, rebuilds the filter store and re-fires
        // onReportStateChange with a fresh array.
        if (reportRef.current && typeof reportRef.current.loadReport === "function") {
            reportRef.current.loadReport();
        }
    }, [clearResults]);

    const noDataText = hasCalculated ? MESSAGES.DEFERRED_REVENUE_NO_RESULTS : "";

    const renderCreateButton = useCallback(
        () => (
            <Button
                className="accordianButton"
                type="default"
                text="Create Unposted AC Journal Transaction"
                onClick={handleCreate}
                disabled={summaryRows.length === 0 || isBusy}
            />
        ),
        [handleCreate, summaryRows.length, isBusy]
    );

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={isBusy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={
                    busy === BUSY.CREATING
                        ? MESSAGES.CREATING_AC_JOURNAL
                        : MESSAGES.CALCULATING_DEFERRED_REVENUE
                }
            />

            <div className="row">
                <div className="col-md-12 main-page">
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>{PAGE_TITLE}</h6>
                            </div>
                        </Item>

                        <Item location="after">
                            <Button className="accordianButton" text="Start" type="default" onClick={handleStart} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text="Reset" onClick={handleReset} disabled={isBusy} />
                        </Item>

                        {/*
                            The legacy print/preview reports are not migrated. The buttons stay for
                            parity but are permanently disabled. The tooltip lives on a wrapping span
                            because DevExtreme's dx-state-disabled sets pointer-events:none, so `hint`
                            alone never shows.
                        */}
                        <Item location="after">
                            <span title={MESSAGES.PRINTING_NOT_AVAILABLE} style={{ display: "inline-block", cursor: "not-allowed" }}>
                                <Button
                                    className="accordianButton"
                                    text="Print Preview"
                                    disabled={true}
                                    hint={MESSAGES.PRINTING_NOT_AVAILABLE}
                                />
                            </span>
                        </Item>
                        <Item location="after">
                            <span title={MESSAGES.PRINTING_NOT_AVAILABLE} style={{ display: "inline-block", cursor: "not-allowed" }}>
                                <Button
                                    className="accordianButton"
                                    text="Print To Printer"
                                    disabled={true}
                                    hint={MESSAGES.PRINTING_NOT_AVAILABLE}
                                />
                            </span>
                        </Item>
                    </Toolbar>
                </div>
            </div>

            {/*
                The context-sensitive help band. In Delphi edtHelpMemo is a sibling of the page control
                with Align = alTop (uWMD0088.dfm), i.e. it sits above ALL five tabs and is re-texted per
                active tab by ChangeHelpText (uWMD0088.pas:721-743) — not nested inside one tab.
            */}
            <div className="op-help">{DEFERRED_REVENUE_HELP[activeTab]}</div>

            {/*
                deferRendering={false} is load-bearing: Start fills all four grids from one response,
                and with the default lazy rendering the unvisited grids would not exist when their
                dataSource prop changes.
            */}
            <TabPanel
                deferRendering={false}
                showNavButtons={true}
                selectedIndex={activeTab}
                onSelectionChanged={(e) => setActiveTab(e.component.option("selectedIndex"))}
            >
                <TabItem title="Calculate">
                    <CalculateTab
                        currentUser={currentUser}
                        dynamicFormSetting={dynamicFormSetting}
                        formFields={formFields}
                        setFormFields={setFormFields}
                        formRefs={formRefs}
                        handleFieldChange={handleFieldChange}
                        handleFormLoad={handleFormLoad}
                        onReportStateChange={handleReportStateChange}
                        reportRef={reportRef}
                    />
                </TabItem>

                <TabItem title="Summary">
                    <DeferredRevenueResultGrid
                        rows={summaryRows}
                        columns={SUMMARY_COLUMNS}
                        keyExpr={KEY_EXPR}
                        noDataText={noDataText}
                        toolbarAction={renderCreateButton}
                    />
                </TabItem>

                <TabItem title="Detail">
                    <DeferredRevenueResultGrid
                        rows={detailRows}
                        columns={DETAIL_COLUMNS}
                        keyExpr={KEY_EXPR}
                        noDataText={noDataText}
                    />
                </TabItem>

                <TabItem title="By Customer">
                    <DeferredRevenueResultGrid
                        rows={customerRows}
                        columns={BY_CUSTOMER_COLUMNS}
                        keyExpr={KEY_EXPR}
                        noDataText={noDataText}
                    />
                </TabItem>

                <TabItem title="By Customer/Dept">
                    <DeferredRevenueResultGrid
                        rows={customerDeptRows}
                        columns={BY_CUSTOMER_DEPT_COLUMNS}
                        keyExpr={KEY_EXPR}
                        noDataText={noDataText}
                    />
                </TabItem>
            </TabPanel>
        </>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CalculateDeferredRevenue);
